import type { LucideIcon } from 'lucide-react';
import { NAV_SECTIONS } from './navigation';

/**
 * Pure logic for the ⌘K command palette: the flat, group-annotated command
 * list (derived from the sidebar's {@link NAV_SECTIONS}, so the palette and the
 * rail stay in sync) and a lightweight fuzzy matcher. No React/DOM here — the
 * {@link CommandPalette} component consumes these.
 */

export interface PaletteCommand {
  /** Route to navigate to (may include a `?query` for a filtered view). */
  path: string;
  /** Display label (the sidebar label). */
  label: string;
  /** Owning nav group heading, shown as a subtle hint. */
  group: string;
  icon: LucideIcon;
}

/** Every navigable destination as a palette command, in sidebar order. */
export const NAV_COMMANDS: PaletteCommand[] = NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({
    path: item.path,
    label: item.label,
    group: section.heading,
    icon: item.icon,
  })),
);

/**
 * Scores `text` against `query` as a case-insensitive subsequence match.
 * Returns a score (higher is better) or `null` when `query` is not a
 * subsequence of `text`. Bonuses reward matches at the start of the string and
 * at word boundaries, plus contiguous runs — so "gh" ranks "Goal Health"
 * highly and a prefix match ("goa" → "Goals") ranks highest.
 */
export function fuzzyScore(text: string, query: string): number | null {
  const q = query.trim().toLowerCase();
  if (q === '') return 0;
  const t = text.toLowerCase();

  let score = 0;
  let ti = 0;
  let prevMatchIndex = -2;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    const found = t.indexOf(ch, ti);
    if (found === -1) return null;

    let charScore = 1;
    if (found === 0) charScore += 8; // very start of the string
    else {
      const prev = t[found - 1];
      if (prev === ' ' || prev === '-' || prev === '/') charScore += 4; // word start
    }
    if (found === prevMatchIndex + 1) charScore += 3; // contiguous with prior

    score += charScore;
    prevMatchIndex = found;
    ti = found + 1;
  }
  return score;
}

export interface RankedCommand {
  command: PaletteCommand;
  score: number;
}

/**
 * Filters + ranks commands for a query. An empty query returns all commands in
 * their natural (sidebar) order. Otherwise commands are matched against
 * "label + group", kept when they match, and sorted by score (desc) with the
 * original order as a stable tiebreak.
 */
export function filterCommands(
  commands: PaletteCommand[],
  query: string,
): PaletteCommand[] {
  const q = query.trim();
  if (q === '') return [...commands];

  const ranked: (RankedCommand & { order: number })[] = [];
  commands.forEach((command, order) => {
    const haystack = `${command.label} ${command.group}`;
    const labelScore = fuzzyScore(command.label, q);
    const haystackScore = fuzzyScore(haystack, q);
    const best =
      labelScore === null
        ? haystackScore
        : haystackScore === null
          ? labelScore
          : Math.max(labelScore, haystackScore);
    if (best !== null) ranked.push({ command, score: best, order });
  });

  ranked.sort((a, b) => b.score - a.score || a.order - b.order);
  return ranked.map((r) => r.command);
}
